import React, { useEffect, useState } from 'react';

import { ClockCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { Button, message, Select, Tabs } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import Input from 'antd/lib/input/Input';
import { Formik, ErrorMessage } from 'formik';
import moment from 'moment';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { createPushSchedule } from '../../hooks/schedules';
import { sendPushNotification, previewPushNotificationAudience } from '../../hooks/settings';
import { getUsersDashboard, getUserLocations } from '../../hooks/users';
import { isAdvancedPushNotificationUser } from '../../utils/isAdvancedPushNotificationUser/isAdvancedPushNotificationUser';
import PushNotificationHistoryTab from './PushNotificationHistoryTab';
import PushScheduleFields from './PushScheduleFields';
import { describeSaveError } from './PushScheduleFields/scheduleErrors';
import { SEND_MODE, validateScheduleFields, buildScheduledAt } from './PushScheduleFields/scheduleValues';
import PushScheduleTab from './PushScheduleTab';

const PUSH_TARGET = {
  ALL: 'item-1',
  USERS: 'item-2',
  REGION: 'item-3',
  HISTORY: 'item-4',
  SCHEDULES: 'item-5',
};

const BRAZIL_REGIONS = [
  { value: 'NORTE', label: 'Norte' },
  { value: 'NORDESTE', label: 'Nordeste' },
  { value: 'CENTRO_OESTE', label: 'Centro-Oeste' },
  { value: 'SUDESTE', label: 'Sudeste' },
  { value: 'SUL', label: 'Sul' },
];

const REGION_STATES = {
  NORTE: ['AC', 'AM', 'AP', 'PA', 'RO', 'RR', 'TO'],
  NORDESTE: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'],
  CENTRO_OESTE: ['DF', 'GO', 'MS', 'MT'],
  SUDESTE: ['ES', 'MG', 'RJ', 'SP'],
  SUL: ['PR', 'RS', 'SC'],
};

const PushNotificationConfigFormWrapper = styled.div`
  background-color: #fff;
  padding: 12px 24px 24px;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);

  @media (min-width: 1024px) {
    width: 980px;
  }

  @media (min-width: 1366px) {
    width: 1240px;
  }

  .ant-tabs {
    width: 100%;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.primary};
  }

  .ant-tabs-tab {
    &:hover,
    &:active,
    &:focus {
      color: ${({ theme }) => theme.primary};
    }
  }

  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.primary};
  }

  .ant-tabs-top > .ant-tabs-nav {
    margin-bottom: 24px;
  }

  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    label:first-child {
      margin-right: 10px;
    }

    .submit_button {
      margin: 24px 0 0 0;
    }

    .submit_button:not([disabled]) {
      background-color: ${({ theme }) => theme.primary};
      border-color: ${({ theme }) => theme.primary};
      display: flex;
      align-self: flex-end;
      border-radius: 3px;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    &:hover,
    &:active,
    &:focus {
      border-color: ${({ theme }) => theme.primary};
    }
  }

  .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const FormLabel = styled.label`
  font-size: 14px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormInputWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const MessageBox = styled.div`
  color: red;
  font-size: 12px;
`;

// Estado limpo do formulário, usado pelos dois ramos de sucesso (envio imediato e
// agendamento). Deixar o formulário preenchido depois de agendar é o que produz
// campanha duplicada: nao ha idempotencia no POST e, em recorrencia semanal, a
// duplicata vira push repetido toda semana.
const emptyFormValues = () => ({
  subject: '',
  message: '',
  userPks: [],
  regionFilter: { regions: [], states: [], cities: [] },
  sendMode: SEND_MODE.NOW,
  scheduleDate: null,
  scheduleTime: null,
  recurrence: 'NONE',
  recurrenceUntil: null,
});

const PushNotificationConfigForm = ({
  initialValues,
  onSuccess,
  onCancel,
  allowScheduling = false,
}) => {
  const advancedPush = isAdvancedPushNotificationUser();
  const [target, setTarget] = useState(PUSH_TARGET.ALL);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLimit, setPreviewLimit] = useState(500);
  const [previewing, setPreviewing] = useState(false);
  // User multi-select — server-driven search by name/email/cpf, no preload.
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  // Cache labels for already-selected users so they remain visible after search clears.
  const [userLabelByPk, setUserLabelByPk] = useState({});
  const [scheduleCount, setScheduleCount] = useState(0);

  useEffect(() => {
    if (target !== PUSH_TARGET.USERS) return undefined;
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      return undefined;
    }
    let cancelled = false;
    setUserSearching(true);
    const timer = setTimeout(() => {
      getUsersDashboard(true, 1, 20, userSearchQuery)
        .then((data) => {
          if (cancelled) return;
          setUserSearchResults(data?.userDashboardList || []);
        })
        .catch(() => { })
        .finally(() => !cancelled && setUserSearching(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [target, userSearchQuery]);

  useEffect(() => {
    if (target !== PUSH_TARGET.REGION || statesList.length > 0) return;
    let cancelled = false;
    setLoadingStates(true);
    getUserLocations('state')
      .then((data) => {
        if (cancelled) return;
        setStatesList((data?.locations || []).map((l) => l.state).filter(Boolean));
      })
      .catch(() => message.error(T('Erro ao carregar estados')))
      .finally(() => !cancelled && setLoadingStates(false));
    return () => { cancelled = true; };
  }, [target, statesList.length]);

  const loadCitiesForStates = async (states) => {
    if (!states || states.length === 0) {
      setCitiesList([]);
      return;
    }
    setLoadingCities(true);
    try {
      const all = await Promise.all(states.map((s) => getUserLocations('city', s)));
      const flat = all.flatMap((r) => r?.locations || []);
      setCitiesList(flat);
    } catch {
      message.error(T('Erro ao carregar cidades'));
    } finally {
      setLoadingCities(false);
    }
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        userPks: [],
        regionFilter: { regions: [], states: [], cities: [] },
      }}
      validate={(values) => {
        const errors = {};
        if (!values.subject) errors.subject = T('O campo título é obrigatório.');
        if (!values.message) errors.message = T('O campo mensagem é obrigatório.');
        if (target === PUSH_TARGET.USERS && (!values.userPks || values.userPks.length === 0)) {
          errors.userPks = T('Selecione ao menos um usuário.');
        }
        if (target === PUSH_TARGET.REGION) {
          const f = values.regionFilter || {};
          const empty =
            (!f.regions || f.regions.length === 0) &&
            (!f.states || f.states.length === 0) &&
            (!f.cities || f.cities.length === 0);
          if (empty) {
            errors.regionFilter = T('Selecione ao menos uma região, estado ou cidade.');
          }
        }
        if (allowScheduling) {
          Object.assign(errors, validateScheduleFields(values));
        }
        return errors;
      }}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setTimeout(async () => {
          if (allowScheduling && values.sendMode === SEND_MODE.SCHEDULE) {
            const scheduledAt = buildScheduledAt(values.scheduleDate, values.scheduleTime);
            // Diferente do ramo de envio imediato (que passa por services/request.js e
            // sempre resolve), este await pode REJEITAR: sem o try/catch a promise vira
            // unhandled rejection, setSubmitting(false) nunca roda e o botao fica
            // loading+disabled para sempre, sem mensagem nenhuma.
            try {
              await createPushSchedule({
                subject: values.subject,
                message: values.message,
                targetType: target === PUSH_TARGET.REGION
                  ? 'REGION'
                  : (target === PUSH_TARGET.USERS ? 'USERS' : 'TENANT'),
                userPks: values.userPks,
                regionFilter: values.regionFilter,
                scheduledAt,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                recurrence: values.recurrence || 'NONE',
                recurrenceUntil: values.recurrenceUntil
                  ? moment(values.recurrenceUntil).format('YYYY-MM-DD')
                  : null,
              });
              message.success(T('Push agendado com sucesso'));
              resetForm({ values: emptyFormValues() });
              setPreviewCount(null);
            } catch (error) {
              message.error(describeSaveError(error, 'CREATE'));
            } finally {
              setSubmitting(false);
            }
            return;
          }

          let payload;
          if (target === PUSH_TARGET.REGION) {
            payload = {
              subject: values.subject,
              message: values.message,
              regionFilter: values.regionFilter,
            };
          } else if (target === PUSH_TARGET.USERS) {
            payload = {
              subject: values.subject,
              message: values.message,
              userPks: values.userPks,
            };
          } else {
            payload = { subject: values.subject, message: values.message };
          }

          const data = await sendPushNotification(payload);

          if (data !== null && data.type === 'success' && !data.data?.error) {
            // MV2-1839: por usuário/região o backend responde assim que valida e grava o push; o
            // envio ao FCM segue em segundo plano e o resultado por destinatário fica no Histórico.
            message.success(
              target === PUSH_TARGET.ALL
                ? T('Push Notification enviado com sucesso!')
                : T('Push Notification em processamento. Acompanhe o resultado na aba Histórico.'),
            );
            resetForm({ values: emptyFormValues() });
            setPreviewCount(null);
            if (onSuccess) onSuccess();
          } else {
            const apiError = (data && data.data && data.data.error) || '';
            const m = apiError.match(/resolved to (\d+) users \(max (\d+) per push\)/);
            if (m) {
              const found = m[1];
              const max = m[2];
              message.error(`${T('O filtro selecionado retornou')} ${found} ${T('usuários, porém o limite máximo por envio é de')} ${max} ${T('destinatários. Refine os filtros da campanha ou utilize a opção de envio para todo o tenant.')}`);
            } else {
              message.error(T('Não foi possível realizar a operação. Certifique-se de que os campos foram corretamente preenchidos.'));
            }
          }
          setSubmitting(false);
        }, 400);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => {
        const renderFields = () => (
          <FieldGroup>
            <FormInputWrapper>
              <FormLabel>{T('Título')}*:</FormLabel>
              <Input
                name='subject'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.subject}
                maxLength={255}
                size='large'
              />
              <ErrorMessage name='subject' component={MessageBox} />
            </FormInputWrapper>

            <FormInputWrapper>
              <FormLabel>{T('Mensagem')}*:</FormLabel>
              <TextArea
                name='message'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.message}
                maxLength={255}
                rows={4}
                size='large'
              />
              <ErrorMessage name='message' component={MessageBox} />
            </FormInputWrapper>

            {allowScheduling && (
              <PushScheduleFields values={values} setFieldValue={setFieldValue} />
            )}
          </FieldGroup>
        );

        return (
          <PushNotificationConfigFormWrapper>
            <form onSubmit={handleSubmit}>
              <Tabs
                activeKey={target}
                onChange={(key) => {
                  setTarget(key);
                  setPreviewCount(null);
                  if (key !== PUSH_TARGET.USERS) setFieldValue('userPks', []);
                  if (key !== PUSH_TARGET.REGION) {
                    setFieldValue('regionFilter', { regions: [], states: [], cities: [] });
                  }
                }}
              >
                <Tabs.TabPane tab={T('Todos')} key={PUSH_TARGET.ALL}>
                  {renderFields()}
                </Tabs.TabPane>

                {advancedPush && <Tabs.TabPane tab={T('Por Usuário')} key={PUSH_TARGET.USERS}>
                  <FieldGroup>
                    <FormInputWrapper>
                      <FormLabel>{T('Usuários')}*:</FormLabel>
                      <Select
                        mode='multiple'
                        showSearch
                        allowClear
                        size='large'
                        loading={userSearching}
                        placeholder={T('Buscar por nome, email ou CPF (mín. 2 caracteres)')}
                        value={values.userPks}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        onSearch={setUserSearchQuery}
                        onChange={(vals, opts) => {
                          setFieldValue('userPks', vals);
                          if (Array.isArray(opts)) {
                            setUserLabelByPk((prev) => {
                              const next = { ...prev };
                              opts.forEach((o) => {
                                if (o?.value != null && o?.label) next[o.value] = o.label;
                              });
                              return next;
                            });
                          }
                          setUserSearchQuery('');
                        }}
                        filterOption={false}
                        notFoundContent={
                          userSearching
                            ? T('Buscando...')
                            : userSearchQuery.length < 2
                              ? T('Digite ao menos 2 caracteres')
                              : T('Nenhum usuário encontrado')
                        }
                        optionLabelProp='label'
                        style={{ width: '100%' }}
                        options={[
                          ...userSearchResults.map((u) => ({
                            value: u.user_pk,
                            label: `${u.user_name || ''} - ${u.email || ''}${u.docNumber ? ' - ' + u.docNumber : ''}`,
                          })),
                          ...values.userPks
                            .filter((pk) => !userSearchResults.some((u) => u.user_pk === pk))
                            .map((pk) => ({
                              value: pk,
                              label: userLabelByPk[pk] || `#${pk}`,
                            })),
                        ]}
                      />
                      <ErrorMessage name='userPks' component={MessageBox} />
                    </FormInputWrapper>
                  </FieldGroup>

                  {renderFields()}
                </Tabs.TabPane>}

                {advancedPush && <Tabs.TabPane tab={T('Por Região')} key={PUSH_TARGET.REGION}>
                  <FieldGroup>
                    <FormInputWrapper>
                      <FormLabel>{T('Regiões')}:</FormLabel>
                      <Select
                        mode='multiple'
                        allowClear
                        size='large'
                        placeholder={T('Selecione uma ou mais regiões (opcional)')}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        value={values.regionFilter.regions}
                        onChange={(v) => {
                          const allowedStates = v.length === 0
                            ? values.regionFilter.states
                            : values.regionFilter.states.filter((s) =>
                              v.some((r) => REGION_STATES[r]?.includes(s)),
                            );
                          const statesRemoved = allowedStates.length !== values.regionFilter.states.length;
                          if (statesRemoved) loadCitiesForStates(allowedStates);
                          setFieldValue('regionFilter', {
                            ...values.regionFilter,
                            regions: v,
                            states: allowedStates,
                            cities: statesRemoved ? [] : values.regionFilter.cities,
                          });
                        }}
                        style={{ width: '100%' }}
                        options={BRAZIL_REGIONS}
                      />
                    </FormInputWrapper>

                    <FormInputWrapper>
                      <FormLabel>{T('Estados')}:</FormLabel>
                      <Select
                        mode='multiple'
                        showSearch
                        allowClear
                        size='large'
                        loading={loadingStates}
                        placeholder={T('Selecione um ou mais estados (opcional)')}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        value={values.regionFilter.states}
                        onChange={(v) => {
                          setFieldValue('regionFilter', { ...values.regionFilter, states: v, cities: [] });
                          loadCitiesForStates(v);
                        }}
                        optionFilterProp='label'
                        style={{ width: '100%' }}
                        options={(values.regionFilter.regions.length === 0
                          ? statesList
                          : statesList.filter((s) =>
                            values.regionFilter.regions.some((r) => REGION_STATES[r]?.includes(s)),
                          )
                        ).map((s) => ({ value: s, label: s }))}
                      />
                    </FormInputWrapper>

                    <FormInputWrapper>
                      <FormLabel>{T('Cidades')}:</FormLabel>
                      <Select
                        mode='multiple'
                        showSearch
                        allowClear
                        size='large'
                        loading={loadingCities}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        disabled={!values.regionFilter.states || values.regionFilter.states.length === 0}
                        placeholder={
                          !values.regionFilter.states || values.regionFilter.states.length === 0
                            ? T('Selecione um estado primeiro')
                            : T('Selecione uma ou mais cidades (opcional)')
                        }
                        value={values.regionFilter.cities}
                        onChange={(v) => setFieldValue('regionFilter', { ...values.regionFilter, cities: v })}
                        optionFilterProp='label'
                        style={{ width: '100%' }}
                        options={citiesList.map((l) => ({
                          value: l.city,
                          label: `${l.city} - ${l.state}`,
                        }))}
                      />
                      <ErrorMessage name='regionFilter' component={MessageBox} />
                    </FormInputWrapper>

                    <FormInputWrapper style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                      <Button
                        type='default'
                        size='large'
                        loading={previewing}
                        onClick={async () => {
                          setPreviewing(true);
                          try {
                            const r = await previewPushNotificationAudience(values.regionFilter);
                            if (r?.type !== 'success') {
                              message.error(T('Erro ao pré-visualizar audiência'));
                              setPreviewing(false);
                              return;
                            }
                            const c = r?.data?.count ?? 0;
                            setPreviewCount(c);
                            const lim = r?.data?.limit ?? 500;
                            setPreviewLimit(lim);
                            if (c > lim) {
                              message.warning(`${T('A audiência')} (${c}) ${T('excede o limite de')} ${lim} ${T('destinatários. Refine o filtro ou use o envio para todo o tenant.')}`);
                            } else if (c === 0) {
                              message.warning(T('Nenhum usuário encontrado para esse filtro'));
                            }
                          } catch {
                            message.error(T('Erro ao pré-visualizar audiência'));
                          } finally {
                            setPreviewing(false);
                          }
                        }}
                      >
                        {T('Pré-visualizar audiência')}
                      </Button>
                      {previewCount !== null && (
                        <span>
                          <strong>{previewCount}</strong> {previewCount === 1 ? T('usuário') : T('usuários')}
                        </span>
                      )}
                      {previewCount !== null && previewCount > previewLimit && (
                        <span style={{ color: '#cf1322', marginLeft: 8 }}>
                          {T('Excede o limite de')} {previewLimit} {T('destinatários. Refine o filtro ou use o envio para todo o tenant.')}
                        </span>
                      )}
                    </FormInputWrapper>
                  </FieldGroup>

                  {renderFields()}
                </Tabs.TabPane>}

                {/*
                  allowScheduling, e nao so advancedPush: este form tambem e renderizado
                  num Modal de 520px pelo StationEditPage, que passa apenas
                  {message, subject}. Sem esta guarda, quem cadastra estacao com a
                  permissao DASH_ADVANCED_PUSH_NOTIFICATION ve "Agendados (0)" dentro
                  daquele modal, dispara GET /schedules e ganha Editar/Cancelar de
                  campanhas globais.
                */}
                {allowScheduling && advancedPush && <Tabs.TabPane
                  tab={
                    <span style={{ color: '#888', fontStyle: 'italic' }}>
                      <ClockCircleOutlined style={{ marginRight: 6 }} />
                      {T('Agendados')} ({scheduleCount})
                    </span>
                  }
                  key={PUSH_TARGET.SCHEDULES}
                >
                  <PushScheduleTab
                    active={target === PUSH_TARGET.SCHEDULES}
                    onCountChange={setScheduleCount}
                  />
                </Tabs.TabPane>}

                {advancedPush && <Tabs.TabPane
                  tab={
                    <span style={{ color: '#888', fontStyle: 'italic' }}>
                      <HistoryOutlined style={{ marginRight: 6 }} />
                      {T('Histórico')}
                    </span>
                  }
                  key={PUSH_TARGET.HISTORY}
                >
                  <PushNotificationHistoryTab active={target === PUSH_TARGET.HISTORY} />
                </Tabs.TabPane>}
              </Tabs>

              {target !== PUSH_TARGET.HISTORY && target !== PUSH_TARGET.SCHEDULES && (
                <Footer>
                  <div style={{ marginTop: 24 }}>* {T('Campos obrigatórios')}</div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    {onCancel && (
                      <Button
                        type='default'
                        size='large'
                        onClick={onCancel}
                        style={{ marginTop: 24 }}
                      >
                        {T('Não enviar')}
                      </Button>
                    )}

                    <Button
                      loading={isSubmitting}
                      size='large'
                      className='submit_button'
                      type='primary'
                      disabled={isSubmitting || (previewCount !== null && previewCount > previewLimit)}
                      onClick={handleSubmit}
                    >
                      {allowScheduling && values.sendMode === SEND_MODE.SCHEDULE
                        ? T('Agendar')
                        : T('Enviar')}
                    </Button>
                  </div>
                </Footer>
              )}
            </form>
          </PushNotificationConfigFormWrapper>
        );
      }}
    </Formik>
  );
};

export default PushNotificationConfigForm;
